import { Link, useLocation } from "react-router-dom";
import { useObserver, useSubtree } from "subtree.js/react";
import { PICTURE_FRAME } from "../components/live/cockpit-frame";
import { BroadcastStageActions, BroadcastStageState } from "./broadcast-stage-controller";

/**
 * Where the camera actually renders: the middle of the cockpit, a bar
 * everywhere else.
 *
 * The `<video>` element is mounted for as long as a broadcast is active and is
 * only hidden off-route, never unmounted. Recreating it would mean re-attaching
 * the published track to a new element, and a preview that has to be rebuilt is
 * a preview that can fail to come back.
 */
export function BroadcastStage() {
  const state = useSubtree(BroadcastStageState);
  const actions = useSubtree(BroadcastStageActions);
  const observer = useObserver();
  const location = useLocation();
  const active = observer.watch(state.active);
  const onAir = observer.watch(state.onAir);

  if (!active) return null;

  // Matched on the whole id, not a prefix of it: `startsWith("/live/streams/abc")`
  // would say a broadcast for stream "abc" belongs on stream "abcdef"'s cockpit.
  const own = `/live/streams/${active.streamId}`;
  const onCockpit = location.pathname === own || location.pathname.startsWith(`${own}/`);

  return (
    <>
      <div className={onCockpit ? `fixed inset-x-0 top-0 z-30 h-[100dvh] bg-black ${PICTURE_FRAME}` : "hidden"}>
        {/* object-contain never crops: what the host frames has to be what the buyers see. */}
        <video ref={actions.attachVideo} className="h-full w-full object-contain" autoPlay playsInline muted />
      </div>

      {/* Off the cockpit and still on air. This exists because of one sentence
          from a real breaker: "I don't even know how to get back to my Stream,
          so if I was hosting something I'd be gone." */}
      {!onCockpit && onAir && (
        <div className="fixed inset-x-0 top-0 z-50 flex items-center gap-3 bg-[color:var(--color-danger)] px-3 py-2 text-sm text-white shadow-lg">
          <span className="flex shrink-0 items-center gap-1.5 font-semibold">
            <span className="size-1.5 animate-pulse rounded-full bg-current" />
            ON AIR
          </span>
          <span className="min-w-0 flex-1 truncate opacity-90">{active.title}</span>
          <Link to={own} className="shrink-0 rounded-md bg-white/20 px-2.5 py-1 text-xs font-medium hover:bg-white/30">
            Back to your stream
          </Link>
          <button
            type="button"
            className="shrink-0 rounded-md bg-white/20 px-2.5 py-1 text-xs font-medium hover:bg-white/30"
            onClick={actions.stopCamera}
          >
            Stop the camera
          </button>
        </div>
      )}
    </>
  );
}
